import React, { useEffect, useState } from 'react';
import { Alert } from 'reactstrap';
import { DragEndEvent } from '@dnd-kit/core';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ClickableIcon, ConfirmDialog, ErrorResponse, Select, Spinner, Toolbar } from '~components';
import { DndWrapper, DraggableItem, DroppableArea } from '~components/drag-and-drop';
import Icon, { faChevronDown, faChevronUp, faGripLines, faPenToSquare, faPlus, faTrash } from '~components/icon';

import { CarePlan, CarePlanGoal, CarePlanLookup, CarePlanNeed } from '~clients/models';

import {
  useDeleteCarePlanGoalMutation,
  useGetCarePlanGoalsQuery,
  useGetCarePlanNeedsQuery,
  useGetGoalApproachTypesQuery,
  useGetGoalStatusesQuery,
  useReorderCarePlanGoalsMutation,
  useUpdateCarePlanGoalMutation,
} from '~clients/actions/carePlans';

import { isCarePlanTabVisible } from '~clients/config/carePlanTypes';

import { bySequence, reorderByDrop } from '../reorder';
import GoalFormModal from './GoalFormModal';
import GoalNumberAvatar from './GoalNumberAvatar';
import LookupBadge from './LookupBadge';

interface ComponentProps {
  plan: CarePlan;
  /** Whether the Goals tab is the visible one (tabs stay mounted while hidden). */
  isActive?: boolean;
}

const byId = <Type extends { id: string }>(rows: Type[]): Map<string, Type> =>
  new Map(rows.map((row) => [row.id, row]));

// Goals tab: numbered, badged goal cards with a quick status change, add/edit/delete, and
// up/down + drag reordering. Mirrors the Assessed Needs tab.
const GoalsTab: React.FC<ComponentProps> = ({ plan, isActive }) => {
  const { data, isLoading, error } = useGetCarePlanGoalsQuery({ planId: plan.id });
  const { data: needs } = useGetCarePlanNeedsQuery({ planId: plan.id });
  const { data: statuses } = useGetGoalStatusesQuery();
  const { data: approachTypes } = useGetGoalApproachTypesQuery();

  const [deleteGoal, { isLoading: isDeleting, error: deleteError, reset: resetDelete }] =
    useDeleteCarePlanGoalMutation();
  const [reorderGoals, { isLoading: isReordering, error: reorderError, reset: resetReorder }] =
    useReorderCarePlanGoalsMutation();
  const [updateGoal, { isLoading: isUpdatingStatus, error: statusError, reset: resetStatus }] =
    useUpdateCarePlanGoalMutation();
  const { showToast } = useToast();

  // Tabs stay mounted, so clear any error banner when the user navigates off this tab.
  useEffect(() => {
    if (isActive === false) {
      resetDelete?.();
      resetReorder?.();
      resetStatus?.();
    }
  }, [isActive, resetDelete, resetReorder, resetStatus]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<CarePlanGoal | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<CarePlanGoal | null>(null);

  const goals = bySequence(data ?? []);
  const needsById = byId<CarePlanNeed>(needs ?? []);
  const statusesById = byId<CarePlanLookup>(statuses ?? []);
  const approachTypesById = byId<CarePlanLookup>(approachTypes ?? []);

  const statusSelectOptions = (statuses ?? []).map((status) => ({ value: status.id, label: status.name }));

  const isSaving = isDeleting || isReordering || isUpdatingStatus;
  const saveError = deleteError ?? reorderError ?? statusError;

  const openAdd = () => {
    setEditing(undefined);
    setIsModalOpen(true);
  };
  const openEdit = (goal: CarePlanGoal) => {
    setEditing(goal);
    setIsModalOpen(true);
  };

  const performDelete = async (goal: CarePlanGoal) => {
    // Clear any stale reorder/status error so the shared banner reflects only this delete's
    // outcome (those hooks hold their last error until re-triggered — see the combined saveError).
    resetReorder();
    resetStatus();
    try {
      await deleteGoal(goal.id).unwrap();
      showToast('success', 'Goal deleted');
    } catch {
      // Surfaced through saveError below.
    }
  };

  const onConfirmDelete = async (confirm: boolean) => {
    const target = pendingDelete;
    setPendingDelete(null);
    if (confirm && target) await performDelete(target);
  };

  // Quick status change — updates only status_id, no modal. No-op when unchanged or cleared.
  const onStatusChange = async (goal: CarePlanGoal, statusId?: string | number | null) => {
    if (!statusId || statusId === goal.status_id) return;
    try {
      await updateGoal({ id: goal.id, status_id: String(statusId) }).unwrap();
      showToast('success', 'Goal status updated');
    } catch {
      // Surfaced through saveError below.
    }
  };

  // Move sourceId to targetId's position and persist. Up/down and drag both route here.
  const persistReorder = (sourceId: string, targetId?: string) => {
    if (!targetId || sourceId === targetId) return;
    reorderGoals({
      planId: plan.id,
      order: reorderByDrop(
        goals.map((goal) => goal.id),
        sourceId,
        targetId,
      ),
    });
  };

  // Drop a dragged card onto another to move it there (up/down is the keyboard equivalent).
  const handleReorderDragEnd = (event: DragEndEvent) => {
    const sourceId = event.active.data.current?.goalId as string | undefined;
    const targetId = event.over ? String(event.over.id) : undefined;
    if (!sourceId) return;
    persistReorder(sourceId, targetId);
  };

  return (
    <div className="section-card-container">
      <Toolbar
        id="care-plan-goals"
        rightCluster={
          <Button size="sm" color="success" dataCy="care-plan-goal-add" onClick={openAdd}>
            <Icon className="me-2" icon={faPlus} />
            New Goal
          </Button>
        }
      />

      {isLoading && (
        <div className="care-plan-section-loading">
          <Spinner loading />
        </div>
      )}
      {error !== undefined && <ErrorResponse dataCy="care-plan-goals-error" message={getErrorMessage(error)} />}
      {saveError !== undefined && (
        <Alert
          color="danger"
          className="care-plan-alert"
          data-cy="care-plan-goals-save-error"
          toggle={() => {
            resetDelete?.();
            resetReorder?.();
            resetStatus?.();
          }}
        >
          {getErrorMessage(saveError)}
        </Alert>
      )}

      {!isLoading && error === undefined && goals.length === 0 && (
        <p className="text-muted care-plan-goals-empty" data-cy="care-plan-goals-empty">
          No goals recorded.
        </p>
      )}

      {!isLoading && error === undefined && goals.length > 0 && (
        <DndWrapper canScroll={false} onDragEnd={handleReorderDragEnd}>
          <div className="care-plan-goals-list">
            {goals.map((goal, index) => {
              const need = goal.need_id ? needsById.get(goal.need_id) : undefined;
              const status = goal.status_id ? statusesById.get(goal.status_id) : undefined;
              const approachType = goal.approach_type_id ? approachTypesById.get(goal.approach_type_id) : undefined;

              return (
                <DroppableArea key={goal.id} id={goal.id} className="care-plan-goal-droppable">
                  {/* 1-based: dnd-kit handles a falsy 0 id unreliably (broke the top card). */}
                  <DraggableItem id={index + 1} data={{ goalId: goal.id }} disabled={isSaving}>
                    <div className="care-plan-goal-row" data-cy={`care-plan-goal-${goal.id}`}>
                      <div className="care-plan-goal-reorder">
                        <GoalNumberAvatar number={index + 1} />
                        <Icon icon={faGripLines} className="care-plan-goal-grip" title="Drag to reorder" aria-hidden />
                        {/* stopPropagation so these controls stay clickable (no drag hijack). */}
                        <div
                          className="care-plan-goal-reorder-buttons"
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <ClickableIcon
                            id={`care-plan-goal-up-${goal.id}`}
                            dataCy={`care-plan-goal-up-${goal.id}`}
                            icon={faChevronUp}
                            size="x-small"
                            disabled={isSaving || index === 0}
                            toolTip="Move up"
                            ariaLabel={`Move goal ${index + 1} up`}
                            onClick={() => persistReorder(goal.id, goals[index - 1]?.id)}
                          />
                          <ClickableIcon
                            id={`care-plan-goal-down-${goal.id}`}
                            dataCy={`care-plan-goal-down-${goal.id}`}
                            icon={faChevronDown}
                            size="x-small"
                            disabled={isSaving || index === goals.length - 1}
                            toolTip="Move down"
                            ariaLabel={`Move goal ${index + 1} down`}
                            onClick={() => persistReorder(goal.id, goals[index + 1]?.id)}
                          />
                        </div>
                      </div>
                      <div className="care-plan-goal-body">
                        <div className="care-plan-goal-heading">
                          <span className="fw-bold">{goal.description}</span>
                          {approachType && (
                            <LookupBadge
                              lookup={approachType}
                              dataCy={`care-plan-goal-approach-${approachType.tag ?? approachType.id}`}
                            />
                          )}
                          {status && (
                            <LookupBadge
                              lookup={status}
                              dataCy={`care-plan-goal-status-badge-${status.tag ?? status.id}`}
                            />
                          )}
                        </div>
                        {need && (
                          <div className="care-plan-goal-meta" data-cy={`care-plan-goal-need-${goal.id}`}>
                            Linked need: {need.title || need.description}
                          </div>
                        )}
                        {goal.approach_description && <div>{goal.approach_description}</div>}
                        {goal.outcome && <div className="text-muted small">Outcome: {goal.outcome}</div>}
                        <div
                          className="care-plan-goal-status-control"
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <Select
                            id={`care-plan-goal-status-select-${goal.id}`}
                            name={`care-plan-goal-status-select-${goal.id}`}
                            ariaLabel={`Goal ${index + 1} status`}
                            size="sm"
                            options={statusSelectOptions}
                            value={goal.status_id ?? null}
                            placeholder="Set status"
                            disabled={isSaving}
                            onChange={(_name, value) => onStatusChange(goal, value)}
                          />
                        </div>
                      </div>
                      <div className="care-plan-goal-actions" onPointerDown={(event) => event.stopPropagation()}>
                        <ClickableIcon
                          id={`care-plan-goal-edit-${goal.id}`}
                          dataCy={`care-plan-goal-edit-${goal.id}`}
                          icon={faPenToSquare}
                          color="secondary"
                          toolTip="Edit goal"
                          ariaLabel={`Edit goal ${index + 1}`}
                          onClick={() => openEdit(goal)}
                        />
                        <ClickableIcon
                          id={`care-plan-goal-delete-${goal.id}`}
                          dataCy={`care-plan-goal-delete-${goal.id}`}
                          icon={faTrash}
                          color="danger"
                          disabled={isSaving}
                          toolTip="Delete goal"
                          ariaLabel={`Delete goal ${index + 1}`}
                          onClick={() => setPendingDelete(goal)}
                        />
                      </div>
                    </div>
                  </DraggableItem>
                </DroppableArea>
              );
            })}
          </div>
        </DndWrapper>
      )}

      <GoalFormModal
        key={editing?.id ?? 'new'}
        planId={plan.id}
        goal={editing}
        hasNeeds={isCarePlanTabVisible(plan.plan_type, 'needs')}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
      />
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        useList={false}
        messages={
          <p>
            <strong>Warning:</strong> This goal will be deleted.
          </p>
        }
        onClose={onConfirmDelete}
      />
    </div>
  );
};

export default GoalsTab;
